// Справочник цехов из БД (единый источник вместо захардкоженных названий).
import { useEffect, useState } from 'react';
import { apiGet } from './api';

export type Ceh = { id: string; name: string; descr: string };

// Короткое имя: часть после «·» («Цех 1 · Очистка» → «Очистка»), иначе полное.
const shortName = (name: string) => name.split('·').pop()?.trim() || name;

// Кэш на уровне модуля — грузим справочник один раз на сессию.
let cache: Promise<Ceh[]> | null = null;
function load(): Promise<Ceh[]> {
  if (!cache)
    cache = apiGet<{ cehs: Ceh[] }>('/api/ref/cehs')
      .then((r) => r.cehs)
      .catch(() => []);
  return cache;
}

// Сбросить кэш после правки названий (редактор в «Настройках»).
export function invalidateCehs() {
  cache = null;
}

// Хук: список цехов + лукап id→имя. До загрузки — пустой список; лукап
// возвращает id как запасной вариант (UI никогда не показывает голый ключ надолго).
export function useCehs() {
  const [list, setList] = useState<Ceh[]>([]);
  useEffect(() => {
    let alive = true;
    const apply = () => load().then((cs) => alive && setList(cs));
    apply();
    // Возврат к вкладке/окну → сбросить кэш и перечитать: если название цеха
    // изменили на другом устройстве, оно подхватится без перезагрузки страницы.
    const refresh = () => {
      if (document.visibilityState === 'hidden') return;
      invalidateCehs();
      apply();
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      alive = false;
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, []);
  const byId = new Map(list.map((c) => [c.id, c] as const));
  return {
    list,
    full: (id: string) => byId.get(id)?.name ?? id,
    short: (id: string) => {
      const n = byId.get(id)?.name;
      return n ? shortName(n) : id;
    },
  };
}
